// Capture-mode chooser for #770. This deliberately runs before any browser
// capture API: choosing a mode is not consent, and no permission prompt should
// appear until the reporter has made an explicit choice and accepted its
// disclosure.

import { en } from './i18n/en.ts';
import type { WidgetMessages } from './i18n/index.ts';

export type CaptureMode = 'screen' | 'camera' | 'audio';

export type CaptureAvailability =
  | { kind: 'inline' }
  | { kind: 'hosted'; explanation: string }
  | { kind: 'unavailable'; explanation: string };

export interface CaptureChoiceAvailability {
  /** Booleans are accepted temporarily for the #770 call sites/tests. */
  screen: CaptureAvailability | boolean;
  camera: CaptureAvailability | boolean;
  audio: CaptureAvailability | boolean;
}

function normalizeAvailability(value: CaptureAvailability | boolean): CaptureAvailability {
  return typeof value === 'boolean'
    ? value
      ? { kind: 'inline' }
      : { kind: 'unavailable', explanation: 'Recording is not available in this browser.' }
    : value;
}

export function showCaptureChoiceModal(
  shadow: ShadowRoot,
  availability: CaptureChoiceAvailability,
  messages: WidgetMessages = en,
): Promise<CaptureMode | null> {
  return new Promise<CaptureMode | null>((resolve) => {
    const previouslyFocused =
      shadow.activeElement instanceof HTMLElement ? shadow.activeElement : null;
    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'oc-capture-choice-title');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483646',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      background: 'rgba(0,0,0,0.55)',
      fontFamily: 'system-ui, sans-serif',
    });

    const dialog = document.createElement('div');
    Object.assign(dialog.style, {
      width: '100%',
      maxWidth: '440px',
      padding: '24px',
      borderRadius: '8px',
      color: '#111827',
      background: '#ffffff',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    });
    const title = document.createElement('h2');
    title.id = 'oc-capture-choice-title';
    title.textContent = messages.recordingChoiceTitle;
    Object.assign(title.style, { margin: '0 0 8px', fontSize: '18px', fontWeight: '600' });
    const lead = document.createElement('p');
    lead.textContent = messages.recordingChoiceLead;
    Object.assign(lead.style, {
      margin: '0 0 16px',
      color: '#4b5563',
      fontSize: '14px',
      lineHeight: '1.5',
    });

    const choices = document.createElement('div');
    Object.assign(choices.style, { display: 'grid', gap: '8px' });
    const buttons: HTMLButtonElement[] = [];
    const addChoice = (
      mode: CaptureMode,
      label: string,
      detail: string,
      rawAvailability: CaptureAvailability | boolean,
    ) => {
      const availability = normalizeAvailability(rawAvailability);
      const supported = availability.kind !== 'unavailable';
      const unavailableDescription = `${mode}-unavailable-description`;
      const wrapper = document.createElement('div');
      wrapper.title = supported ? '' : availability.explanation;
      const button = document.createElement('button');
      button.type = 'button';
      button.disabled = !supported;
      button.setAttribute('data-oc-capture-mode', mode);
      if (!supported) button.setAttribute('aria-describedby', unavailableDescription);
      button.textContent = supported
        ? `${label} — ${detail}${availability.kind === 'hosted' ? ' Opens a secure OnCatch recorder.' : ''}`
        : `${label} — ${messages.recordingChoiceUnavailable}`;
      Object.assign(button.style, {
        width: '100%',
        padding: '12px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        color: supported ? '#111827' : '#6b7280',
        background: supported ? '#ffffff' : '#f3f4f6',
        textAlign: 'left',
        fontSize: '14px',
        cursor: supported ? 'pointer' : 'not-allowed',
      });
      button.addEventListener('click', () => close(mode));
      wrapper.appendChild(button);
      if (!supported) {
        const description = document.createElement('span');
        description.id = unavailableDescription;
        description.textContent = availability.explanation;
        Object.assign(description.style, {
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: '0',
        });
        wrapper.appendChild(description);
      }
      choices.appendChild(wrapper);
      if (supported) buttons.push(button);
    };

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = 'Cancel';
    Object.assign(cancel.style, {
      marginTop: '16px',
      padding: '8px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      background: '#ffffff',
      color: '#374151',
      fontSize: '14px',
      cursor: 'pointer',
    });

    function close(choice: CaptureMode | null): void {
      shadow.removeEventListener('keydown', onKeyDown as EventListener);
      overlay.remove();
      previouslyFocused?.focus();
      resolve(choice);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(null);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = [...buttons, cancel];
      const focused = shadow.activeElement as HTMLElement | null;
      const index = focused ? focusables.indexOf(focused as HTMLButtonElement) : -1;
      if (event.shiftKey && index <= 0) {
        event.preventDefault();
        focusables[focusables.length - 1]?.focus();
      } else if (!event.shiftKey && index === focusables.length - 1) {
        event.preventDefault();
        focusables[0]?.focus();
      }
    }

    addChoice(
      'screen',
      messages.recordingChoiceScreen,
      messages.recordingChoiceScreenDetail,
      availability.screen,
    );
    addChoice(
      'camera',
      messages.recordingChoiceCamera,
      messages.recordingChoiceCameraDetail,
      availability.camera,
    );
    addChoice(
      'audio',
      messages.recordingChoiceAudio,
      messages.recordingChoiceAudioDetail,
      availability.audio,
    );
    cancel.addEventListener('click', () => close(null));
    shadow.addEventListener('keydown', onKeyDown as EventListener);
    dialog.append(title, lead, choices, cancel);
    overlay.appendChild(dialog);
    shadow.appendChild(overlay);
    (buttons[0] ?? cancel).focus();
  });
}
